import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SpApiSection from '../components/SpApiSection';
import Icon from '../components/Icon';

const STATUS_MAP = {
  active: { label: 'Aktif', color: '#34D399' },
  paused: { label: 'Duraklatılmış', color: '#F2B84B' },
  churned: { label: 'Churned', color: '#FF6B5B' },
  inactive: { label: 'Pasif', color: '#74746D' },
  internal: { label: 'İç grup', color: '#7AA2F7' },
};

// Slack workspace (BoldRanks) — kanal ID'sinden doğrudan link
const SLACK_TEAM_ID = 'T065R1CJM2B';

const ONBOARDING_STATUS = {
  pending: { label: 'Beklemede', color: '#9A9A92' },
  in_progress: { label: 'Devam Ediyor', color: '#D4FF3A' },
  emails_sent: { label: 'Mailler Gönderildi', color: '#A78BFA' },
  completed: { label: 'Tamamlandı', color: '#34D399' },
};

export default function AccountDetail({ accountId, onClose, onUpdated }) {
  const { user, can } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Faturalandırma + Stripe eşleştirme: admin ve finance
  const canBilling = can('billing.view');
  const canBillingEdit = can('billing.edit');
  const canIntegrations = can('accounts.integrations');
  const [account, setAccount] = useState(null);
  const [hasV2Channels, setHasV2Channels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('overview');
  const [checklist, setChecklist] = useState({
    language: 'TR',
    subject_topic: '',
    company_setup: false,
    cpa_firm: 'none',
    mercury_setup: false,
    incentive: false,
  });
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [previewJobs, setPreviewJobs] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);

  const fetchAccount = useCallback(async () => {
    try {
      const res = await api.get(`/accounts/${accountId}`);
      setAccount(res.data);
      if (res.data.onboarding) {
        setChecklist({
          language: res.data.onboarding.language || 'TR',
          subject_topic: res.data.onboarding.subject_topic || '',
          company_setup: !!res.data.onboarding.company_setup,
          cpa_firm: res.data.onboarding.cpa_firm || 'none',
          mercury_setup: !!res.data.onboarding.mercury_setup,
          incentive: !!res.data.onboarding.incentive,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);
  useEffect(() => { api.get('/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const saveManager = async (field, val) => {
    await api.put(`/accounts/${accountId}`, { [field]: val === '' ? null : Number(val) });
    await fetchAccount();
    if (onUpdated) onUpdated();
  };

  const saveChecklist = async () => {
    setSavingChecklist(true);
    try {
      const payload = { ...checklist };
      if (!payload.company_setup) payload.cpa_firm = 'none';
      await api.put(`/accounts/${accountId}/onboarding`, payload);
      await fetchAccount();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error(err);
      alert('Checklist kaydedilemedi');
    } finally {
      setSavingChecklist(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.get(`/accounts/${accountId}/onboarding/preview`);
      setPreviewJobs(res.data.jobs || []);
    } catch (err) {
      console.error(err);
      alert('Preview yüklenemedi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendEmails = async () => {
    setSending(true);
    try {
      const res = await api.post(`/accounts/${accountId}/onboarding/send`);
      alert(`${res.data.count} mail gönderim için kuyruğa alındı`);
      setConfirmSend(false);
      await fetchAccount();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error(err);
      alert('Mail gönderilemedi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (tab === 'emails' && account) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, checklist.language, checklist.company_setup, checklist.cpa_firm, checklist.mercury_setup, checklist.incentive, checklist.subject_topic]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal--large" onClick={(e) => e.stopPropagation()}>
          <div className="page-loading">Yükleniyor...</div>
        </div>
      </div>
    );
  }
  if (!account) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 980 }}>
        <div className="modal__header">
          <div>
            <h2 className="modal__title">{account.company_name}</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              {account.brand_name && (
                <span className="text-muted" style={{ fontSize: 13 }}>Marka: {account.brand_name}</span>
              )}
              <span className="badge" style={{
                background: ONBOARDING_STATUS[account.onboarding_status]?.color + '22',
                color: ONBOARDING_STATUS[account.onboarding_status]?.color,
              }}>
                {ONBOARDING_STATUS[account.onboarding_status]?.label || account.onboarding_status}
              </span>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #17191C', marginBottom: 20 }}>
          {[
            { key: 'overview', label: 'Genel' },
            { key: 'contacts', label: `Kontaklar (${account.contacts?.length || 0})` },
            { key: 'checklist', label: 'Onboarding Checklist' },
            { key: 'emails', label: 'Mail Preview & Gönder' },
            ...(canBilling ? [{ key: 'billing', label: 'Faturalandırma' }] : []),
            { key: 'logs', label: `Geçmiş (${account.email_logs?.length || 0})` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: 'none',
                border: 'none',
                color: tab === t.key ? '#D4FF3A' : '#9A9A92',
                padding: '10px 16px',
                cursor: 'pointer',
                fontWeight: tab === t.key ? 600 : 400,
                borderBottom: tab === t.key ? '2px solid #D4FF3A' : '2px solid transparent',
                fontSize: 14,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <>
            <div className="modal__grid">
              {[
                { label: 'İsim', value: account.first_name },
                { label: 'Şirket', value: account.company_name },
                { label: 'Marka', value: account.brand_name },
                { label: 'Satışcı', value: account.sales_name },
                { label: 'Sellerboard E-mail', value: account.sellerboard_email },
                {
                  label: 'Google Drive Folder', value: account.gdrive_folder_id,
                  href: account.gdrive_folder_id && `https://drive.google.com/drive/folders/${account.gdrive_folder_id}`,
                },
                {
                  label: 'Slack Channel', value: account.slack_channel_id,
                  href: account.slack_channel_id && `https://app.slack.com/client/${SLACK_TEAM_ID}/${account.slack_channel_id}`,
                },
                { label: 'Amazon Seller ID', value: account.amazon_seller_id },
                {
                  label: 'Rapor Tablosu (Total / Daily)', value: account.total_report_sheet_id,
                  href: account.total_report_sheet_id && `https://docs.google.com/spreadsheets/d/${account.total_report_sheet_id}`,
                },
                {
                  label: 'Rapor Tablosu (Last Day)', value: account.last_day_report_sheet_id,
                  href: account.last_day_report_sheet_id && `https://docs.google.com/spreadsheets/d/${account.last_day_report_sheet_id}`,
                },
              ].map(item => (
                <div key={item.label} className="modal__field">
                  <p className="modal__field-label">{item.label}</p>
                  {item.href ? (
                    <a className="modal__field-value" href={item.href} target="_blank" rel="noreferrer"
                      style={{ color: '#D4FF3A', textDecoration: 'none', wordBreak: 'break-all' }}>{item.value} ↗</a>
                  ) : (
                    <p className="modal__field-value" style={{ wordBreak: 'break-all' }}>{item.value || '—'}</p>
                  )}
                </div>
              ))}
              <ManagerField label="Account Manager" value={account.am_id} display={account.am_name}
                users={users} editable={can('accounts.assign')} onSave={(v) => saveManager('am_id', v)} />
              <ManagerField label="PPC Manager" value={account.ppc_id} display={account.ppc_name}
                users={users} editable={can('accounts.assign')} onSave={(v) => saveManager('ppc_id', v)} />
              <div className="modal__field">
                <p className="modal__field-label">Durum</p>
                <StatusSelect
                  value={account.status}
                  editable={can('accounts.status')}
                  onSave={async (next) => {
                    await api.put(`/accounts/${accountId}`, { status: next });
                    await fetchAccount();
                    if (onUpdated) onUpdated();
                  }}
                />
              </div>
              <div className="modal__field">
                <p className="modal__field-label">Müşteri Sağlığı</p>
                {isAdmin || account.am_id === user?.id ? (
                  <select className="form-input" style={{ width: 'auto', padding: '4px 10px', fontSize: 13 }} value={(account.client_health || '').toLowerCase()}
                    onChange={async (e) => { await api.put(`/accounts/${accountId}`, { client_health: e.target.value || null }); await fetchAccount(); if (onUpdated) onUpdated(); }}>
                    <option value="">Belirtilmedi</option>
                    <option value="green">Yeşil — iyi gidiyor</option>
                    <option value="yellow">Sarı — dikkat</option>
                    <option value="red">Kırmızı — risk</option>
                  </select>
                ) : (
                  <p className="modal__field-value">{({ green: 'Yeşil', yellow: 'Sarı', red: 'Kırmızı' })[(account.client_health || '').toLowerCase()] || '—'}</p>
                )}
              </div>
              <EditableTextField
                label="Stripe Customer ID"
                value={canBilling ? account.stripe_customer_id : (account.has_stripe_customer ? 'Eşleşmiş' : null)}
                editable={canBillingEdit}
                placeholder="cus_..."
                onSave={async (v) => {
                  await api.put(`/accounts/${accountId}`, { stripe_customer_id: v });
                  await fetchAccount();
                  if (onUpdated) onUpdated();
                }}
              />
              {canBillingEdit && !account.stripe_customer_id && (
                <StripeSyncField
                  accountId={accountId}
                  onLinked={async () => { await fetchAccount(); if (onUpdated) onUpdated(); }}
                />
              )}
            </div>

            <SlackChannelsPanel accountId={accountId} isAdmin={canIntegrations} onLoaded={setHasV2Channels}
              onChanged={() => { fetchAccount(); if (onUpdated) onUpdated(); }} />

            {/* Eski (7 kanallı) kurulum: sadece yeni kanal kaydı olmayan markalarda */}
            {canIntegrations && !hasV2Channels && (!account.slack_channel_id || !account.gdrive_folder_id) && (
              <IntegrationRetry accountId={accountId} />
            )}

            <SpApiSection
              accountId={accountId}
              hasToken={!!account.has_sp_api_token}
              marketplaceIds={account.marketplace_ids || []}
              isAdmin={canIntegrations}
              onChange={() => { fetchAccount(); if (onUpdated) onUpdated(); }}
            />
          </>
        )}

        {/* CONTACTS */}
        {tab === 'contacts' && (
          <ContactsTab
            accountId={accountId}
            contacts={account.contacts || []}
            canDelete={can('contacts.delete_any') || account.am_id === user?.id}
            onChange={() => { fetchAccount(); if (onUpdated) onUpdated(); }}
          />
        )}

        {/* CHECKLIST */}
        {tab === 'checklist' && (
          <div>
            <div className="form-group">
              <label className="form-label">Mail Konu Başlığı</label>
              <input
                className="form-input"
                value={checklist.subject_topic}
                onChange={(e) => setChecklist({ ...checklist, subject_topic: e.target.value })}
                placeholder="Örn: Yeni Başarı Hikayeniz İçin Start Veriyoruz"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Dil</label>
              <select
                className="form-input"
                value={checklist.language}
                onChange={(e) => setChecklist({ ...checklist, language: e.target.value })}
              >
                <option value="TR">Türkçe</option>
                <option value="EN">English</option>
              </select>
            </div>

            <ToggleRow
              label="Şirket Kurulacak mı?"
              value={checklist.company_setup}
              onChange={(v) => setChecklist({ ...checklist, company_setup: v, cpa_firm: v ? checklist.cpa_firm : 'none' })}
            />

            {checklist.company_setup && (
              <div className="form-group">
                <label className="form-label">CPA Firması</label>
                <select
                  className="form-input"
                  value={checklist.cpa_firm}
                  onChange={(e) => setChecklist({ ...checklist, cpa_firm: e.target.value })}
                >
                  <option value="none">Seçilmedi</option>
                  <option value="manay">Manay CPA</option>
                  <option value="sky">Sky Accounting</option>
                </select>
              </div>
            )}

            <ToggleRow
              label="Mercury Hesabı Açılacak mı?"
              value={checklist.mercury_setup}
              onChange={(v) => setChecklist({ ...checklist, mercury_setup: v })}
            />

            <ToggleRow
              label="Teşvik Çalışılacak mı?"
              value={checklist.incentive}
              onChange={(v) => setChecklist({ ...checklist, incentive: v })}
            />

            <button
              className="btn btn--primary"
              style={{ marginTop: 16 }}
              onClick={saveChecklist}
              disabled={savingChecklist}
            >
              {savingChecklist ? 'Kaydediliyor...' : 'Checklist Kaydet'}
            </button>
          </div>
        )}

        {/* EMAILS PREVIEW */}
        {tab === 'emails' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <p style={{ color: '#9A9A92', fontSize: 13, margin: 0 }}>
                  Checklist'e göre gönderilecek mailler. Göndermeden önce her birini açıp inceleyebilirsin.
                </p>
              </div>
              <button className="btn btn--secondary" onClick={loadPreview} disabled={loadingPreview}>
                {loadingPreview ? 'Yükleniyor...' : '↻ Yenile'}
              </button>
            </div>

            {previewJobs.length === 0 && !loadingPreview && (
              <div className="text-muted" style={{ textAlign: 'center', padding: 32 }}>
                Henüz mail planı yok. Checklist'i doldurup kaydet, sonra önizleme oluşacak.
              </div>
            )}

            {previewJobs.map((job, idx) => (
              <div key={idx} className="card" style={{ marginBottom: 12, padding: 16 }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setExpandedJob(expandedJob === idx ? null : idx)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A' }}>
                        {job.template_key}
                      </span>
                      <strong style={{ color: '#F2EFE6' }}>{job.subject || '(konusuz)'}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#9A9A92' }}>
                      To: {job.to?.map(r => r.email).join(', ') || '—'}
                      {job.cc?.length > 0 && <> · CC: {job.cc.map(r => r.email).join(', ')}</>}
                    </div>
                  </div>
                  <span style={{ color: '#9A9A92' }}>{expandedJob === idx ? '▲' : '▼'}</span>
                </div>
                {expandedJob === idx && (
                  <div style={{
                    marginTop: 16, padding: 16, background: '#0A0B0D',
                    border: '1px solid #17191C', borderRadius: 8,
                    whiteSpace: 'pre-wrap', fontFamily: 'monospace',
                    fontSize: 13, color: '#C9C7BE', maxHeight: 400, overflowY: 'auto',
                  }}>
                    {job.body}
                  </div>
                )}
              </div>
            ))}

            {previewJobs.length > 0 && (
              <div style={{ marginTop: 24, padding: 16, background: '#0A0B0D', borderRadius: 8, border: '1px solid #17191C' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={confirmSend}
                    onChange={(e) => setConfirmSend(e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ color: '#F2EFE6' }}>
                    Onaylıyorum — yukarıdaki {previewJobs.length} mail kontaklara gönderilsin
                  </span>
                </label>
                <button
                  className="btn btn--primary"
                  style={{ marginTop: 12, width: '100%' }}
                  onClick={sendEmails}
                  disabled={!confirmSend || sending}
                >
                  {sending ? 'Gönderiliyor...' : <><Icon name="send" size={14} /> Onboarding Maillerini Gönder</>}
                </button>
                {account.onboarding?.emails_sent_at && (
                  <p style={{ color: '#F2B84B', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                    <Icon name="alert" size={14} /> Bu hesap için mailler daha önce {new Date(account.onboarding.emails_sent_at).toLocaleString('tr-TR')} tarihinde gönderildi
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* BILLING */}
        {tab === 'billing' && canBilling && (
          <BillingTab
            accountId={accountId}
            isAdmin={canBillingEdit}
            stripeCustomerId={account.stripe_customer_id}
            contacts={account.contacts || []}
            canDelete={can('contacts.delete_any') || account.am_id === user?.id}
            onChange={() => { fetchAccount(); if (onUpdated) onUpdated(); }}
          />
        )}

        {/* LOGS */}
        {tab === 'logs' && (
          <div>
            {(!account.email_logs || account.email_logs.length === 0) && (
              <div className="text-muted" style={{ textAlign: 'center', padding: 32 }}>
                Henüz mail gönderimi yapılmadı.
              </div>
            )}
            {account.email_logs?.map(log => (
              <div key={log.id} className="card" style={{ marginBottom: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <strong style={{ color: '#F2EFE6' }}>{log.subject || log.template_key}</strong>
                  <span className="badge" style={{
                    background: log.status === 'sent' ? '#34D39922' : log.status === 'failed' ? '#FF6B5B22' : '#D4FF3A22',
                    color: log.status === 'sent' ? '#34D399' : log.status === 'failed' ? '#FF6B5B' : '#D4FF3A',
                  }}>
                    {log.status}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#9A9A92' }}>
                  {new Date(log.created_at).toLocaleString('tr-TR')}
                  {log.sent_at && ` · gönderildi: ${new Date(log.sent_at).toLocaleString('tr-TR')}`}
                </div>
                {log.error_message && (
                  <div style={{ fontSize: 12, color: '#FF6B5B', marginTop: 4 }}>Hata: {log.error_message}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, value, onChange }) {
  return (
    <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #17191C' }}>
      <label className="form-label" style={{ margin: 0 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => onChange(true)}
          style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: value ? '#34D399' : '#17191C',
            color: value ? '#0E0F11' : '#9A9A92', fontWeight: 600,
          }}
        >Evet</button>
        <button
          type="button"
          onClick={() => onChange(false)}
          style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: !value ? '#FF6B5B' : '#17191C',
            color: !value ? '#0E0F11' : '#9A9A92', fontWeight: 600,
          }}
        >Hayır</button>
      </div>
    </div>
  );
}

// Yeni marka Slack kanalları (v2): durum, Slack'te aç, üyeler; hatalı/bekleyenler için yeniden dene (admin)
const CH_STATUS = {
  created: { label: 'Açıldı', color: '#34D399' },
  exists: { label: 'Zaten vardı', color: '#7DB4FF' },
  pending: { label: 'Bekliyor', color: '#F2B84B' },
  error: { label: 'Hata', color: '#FF6B5B' },
};
function SlackChannelsPanel({ accountId, isAdmin, onLoaded, onChanged }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    api.get(`/accounts/${accountId}/slack-channels`).then(r => { setList(r.data); if (onLoaded) onLoaded(r.data.length > 0); }).catch(() => setList([]));
  }, [accountId, onLoaded]);
  useEffect(() => { load(); }, [load]);
  if (!list || list.length === 0) return null;
  const retry = async () => {
    setBusy(true); setMsg('');
    try { await api.post(`/accounts/${accountId}/slack-channels/retry`); setMsg('n8n tetiklendi; birkaç dakika içinde durum güncellenir.'); setTimeout(() => { load(); if (onChanged) onChanged(); }, 8000); }
    catch (e) { setMsg('Hata: ' + (e.response?.data?.detail || e.response?.data?.error || e.message)); }
    setBusy(false);
  };
  const needsRetry = list.some(c => c.status === 'pending' || c.status === 'error');
  return (
    <div style={{ margin: '14px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>Slack kanalları</strong>
        {isAdmin && needsRetry && (
          <button type="button" className="btn btn--secondary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={retry} disabled={busy}>
            {busy ? 'Tetikleniyor…' : '⟳ Bekleyen/hatalıları yeniden dene'}
          </button>
        )}
        {msg && <span className="text-muted" style={{ fontSize: 12 }}>{msg}</span>}
      </div>
      {list.map(c => {
        const st = CH_STATUS[c.status] || CH_STATUS.pending;
        return (
          <div key={c.id} className="sc-card" style={{ marginBottom: 8 }}>
            <div className="sc-card__head" style={{ marginBottom: 6 }}>
              <span className="sc-name">#{c.name}</span>
              {c.is_client && <span className="sc-badge">Müşteriyle ortak</span>}
              <span style={{ color: st.color, fontSize: 12, fontWeight: 600 }}>{st.label}</span>
              {c.slack_channel_id && (
                <a href={`https://app.slack.com/client/${SLACK_TEAM_ID}/${c.slack_channel_id}`} target="_blank" rel="noreferrer"
                  style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--accent)' }}>Slack'te aç ↗</a>
              )}
            </div>
            <div className="sc-members">
              {c.members.map(m => (
                <span key={m.id} className={`sc-chip ${m.has_slack ? '' : 'is-missing'}`} style={{ paddingRight: 8 }} title={m.has_slack ? '' : 'Slack ID eksik: davet edilemedi'}>{m.name}</span>
              ))}
              {c.members.length === 0 && <span className="text-muted" style={{ fontSize: 12 }}>Üye seçilmemiş</span>}
            </div>
            {c.error && <div className="sc-note">{c.error}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Account Manager / PPC Manager atama alanı (kullanıcı dropdown)
// Hesap kurulumu (Drive klasörü, Slack kanalları) yarıda kaldıysa n8n'i yeniden tetikler — sadece admin.
function IntegrationRetry({ accountId }) {
  const [state, setState] = useState(null);
  const run = async () => {
    if (!window.confirm(
      'Hesap kurulumu (Drive klasörü + Slack kanalları) yeniden çalıştırılacak.\n\n' +
      'Kanallar zaten açılmışsa Slack "name_taken" hatası verebilir; Drive\'da ikinci klasör oluşabilir.\n' +
      'Önce n8n\'de son çalışmanın nerede durduğuna bakmanız önerilir. Devam edilsin mi?'
    )) return;
    setState('busy');
    try {
      await api.post(`/accounts/${accountId}/integrations/retry`);
      setState('ok');
    } catch (err) {
      setState('Hata: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  };
  return (
    <div style={{ margin: '12px 0', padding: 12, border: '1px dashed #F2B84B66', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ color: '#F2B84B', fontSize: 13 }}><Icon name="alert" size={14} /> Slack kanalı veya Drive klasörü CRM'e bağlanmamış — kurulum yarıda kalmış olabilir.</span>
      <button onClick={run} disabled={state === 'busy'}
        style={{ padding: '6px 12px', background: '#2A2D32', color: '#F2EFE6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
        {state === 'busy' ? 'Tetikleniyor…' : '⟳ Kurulumu yeniden çalıştır'}
      </button>
      {state === 'ok' && <span style={{ color: '#34D399', fontSize: 12 }}>n8n tetiklendi — birkaç dakika içinde bağlantılar gelmeli.</span>}
      {state && state !== 'ok' && state !== 'busy' && <span style={{ color: '#FF6B5B', fontSize: 12 }}>{state}</span>}
    </div>
  );
}

// Stripe'ta faturalandırma e-postasıyla müşteri arar (sadece okuma); tek eşleşmede ID'yi doldurur.
// Fatura KESMEZ. Birden çok eşleşmede kullanıcı seçer.
function StripeSyncField({ accountId, onLinked }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async (customerId) => {
    setBusy(true);
    try {
      const res = await api.post(`/accounts/${accountId}/stripe-sync`, customerId ? { customer_id: customerId } : {});
      setResult(res.data);
      if (res.data.status === 'linked' || res.data.status === 'already') await onLinked();
    } catch (err) {
      setResult({ status: 'error', error: err.response?.data?.error || err.message });
    } finally {
      setBusy(false);
    }
  };
  const msg = (() => {
    if (!result) return null;
    switch (result.status) {
      case 'linked': return { color: '#34D399', text: `Bulundu ve kaydedildi: ${result.customer.id} (${result.email})` };
      case 'already': return { color: '#34D399', text: `Zaten eşleşmiş: ${result.stripe_customer_id}` };
      case 'not_found': return { color: '#F2B84B', text: `Stripe'ta ${result.email} ile müşteri yok. Stripe'taki e-postayı kontrol edin ya da ID'yi elle girin.` };
      case 'no_email': return { color: '#F2B84B', text: 'Hesapta faturalandırma/birincil kontak e-postası yok.' };
      case 'multiple': return { color: '#F2B84B', text: `${result.email} ile birden çok müşteri bulundu — doğru olanı seçin:` };
      default: return { color: '#FF6B5B', text: `Eşleştirilemedi: ${result.error}` };
    }
  })();
  return (
    <div className="modal__field">
      <p className="modal__field-label">Stripe Eşleştirme</p>
      <button
        onClick={() => run()}
        disabled={busy}
        style={{ padding: '7px 12px', background: '#D4FF3A', color: '#0E0F11', border: 'none', borderRadius: 6, cursor: busy ? 'default' : 'pointer', fontSize: 13 }}
      >{busy ? 'Aranıyor…' : '⟳ Senkronize Et'}</button>
      {msg && <p style={{ color: msg.color, fontSize: 12, marginTop: 6 }}>{msg.text}</p>}
      {result?.status === 'multiple' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {result.candidates.map(c => (
            <button key={c.id} onClick={() => run(c.id)} disabled={busy}
              style={{ textAlign: 'left', padding: '6px 10px', background: '#17191C', color: '#F2EFE6', border: '1px solid #2A2D32', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              {c.id} — {c.name || 'isimsiz'} {c.created ? `(${new Date(c.created).toLocaleDateString('tr-TR')})` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableTextField({ label, value, editable, placeholder, onSave }) {
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setVal(value || ''); }, [value]);
  const dirty = val.trim() !== (value || '');
  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try { await onSave(val.trim()); setSaved(true); setTimeout(() => setSaved(false), 1500); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal__field">
      <p className="modal__field-label">{label}</p>
      {editable ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder={placeholder}
            style={{
              flex: 1, padding: '7px 10px', background: '#17191C',
              border: '1px solid #2A2D32', borderRadius: 6, color: '#F2EFE6',
              fontSize: 14, outline: 'none',
            }}
          />
          <button
            onClick={save}
            disabled={saving || !dirty}
            style={{
              padding: '7px 12px', background: dirty ? '#D4FF3A' : '#2A2D32', color: dirty ? '#0E0F11' : '#C9C7BE',
              border: 'none', borderRadius: 6, cursor: (saving || !dirty) ? 'default' : 'pointer', fontSize: 13,
            }}
          >{saving ? '…' : (saved ? '✓' : 'Kaydet')}</button>
        </div>
      ) : (
        <p className="modal__field-value" style={{ wordBreak: 'break-all' }}>{value || '—'}</p>
      )}
    </div>
  );
}

function ManagerField({ label, value, display, users, editable, onSave }) {
  const [saving, setSaving] = useState(false);
  const change = async (e) => {
    setSaving(true);
    try { await onSave(e.target.value); } finally { setSaving(false); }
  };
  return (
    <div className="modal__field">
      <p className="modal__field-label">{label}</p>
      {editable ? (
        <select
          value={value || ''}
          disabled={saving}
          onChange={change}
          style={{
            width: '100%', padding: '7px 10px', background: '#17191C',
            border: '1px solid #2A2D32', borderRadius: 6, color: '#F2EFE6',
            fontSize: 14, outline: 'none', cursor: saving ? 'wait' : 'pointer',
          }}
        >
          <option value="">— Atanmadı —</option>
          {(users || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      ) : (
        <p className="modal__field-value">{display || '—'}</p>
      )}
    </div>
  );
}

function StatusSelect({ value, editable, onSave }) {
  const [saving, setSaving] = useState(false);
  const opt = STATUS_MAP[value] || { label: value, color: '#9A9A92' };

  const handleChange = async (e) => {
    const next = e.target.value;
    if (next === value) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch (err) {
      alert('Durum güncellenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!editable) {
    return (
      <span className="badge" style={{
        background: opt.color + '22', color: opt.color,
        fontSize: 12, fontWeight: 600, padding: '4px 10px',
      }}>
        {opt.label}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select
        value={value}
        onChange={handleChange}
        disabled={saving}
        style={{
          padding: '4px 8px', background: '#17191C',
          border: `1px solid ${opt.color}55`, borderRadius: 6,
          color: opt.color, fontSize: 13, fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
        }}
      >
        <option value="active">Aktif</option>
        <option value="paused">Duraklatılmış</option>
        <option value="churned">Churned</option>
        <option value="inactive">Pasif</option>
        <option value="internal">İç grup</option>
      </select>
      {saving && <span style={{ color: '#9A9A92', fontSize: 11 }}>...</span>}
    </div>
  );
}

function ContactsTab({ accountId, contacts, canDelete, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: '', is_primary: false });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounts/${accountId}/contacts`, form);
      setForm({ name: '', email: '', role: '', is_primary: false });
      setAdding(false);
      onChange();
    } catch (err) {
      alert('Kontak eklenemedi');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Kontağı silmek istediğinden emin misin?')) return;
    try {
      await api.delete(`/accounts/contacts/${id}`);
      onChange();
    } catch (err) {
      // hata mesajı api katmanında gösterilir
    }
  };

  return (
    <div>
      {contacts.length === 0 && !adding && (
        <div className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
          Henüz kontak yok.
        </div>
      )}

      {contacts.map(c => (
        <div key={c.id} className="card" style={{ marginBottom: 8, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ color: '#F2EFE6' }}>{c.name}</strong>
              {c.is_primary && <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A' }}>Birincil</span>}
              {c.role && <span style={{ fontSize: 12, color: '#9A9A92' }}>· {c.role}</span>}
            </div>
            <div style={{ fontSize: 13, color: '#9A9A92', marginTop: 4 }}>{c.email}</div>
          </div>
          {canDelete && (
            <button onClick={() => remove(c.id)} title="Kontağı sil" style={{ background: 'none', border: 'none', color: '#FF6B5B', cursor: 'pointer', fontSize: 18 }}>×</button>
          )}
        </div>
      ))}

      {adding ? (
        <form onSubmit={submit} className="card" style={{ padding: 16, marginTop: 12 }}>
          <div className="form-group">
            <label className="form-label">İsim</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">E-posta</label>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Rol</label>
            <input className="form-input" placeholder="Sahip, Finans, Operasyon..." value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#C9C7BE' }}>
            <input type="checkbox" checked={form.is_primary} onChange={e => setForm({ ...form, is_primary: e.target.checked })} />
            Birincil kontak
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn--primary" style={{ flex: 1 }}>Ekle</button>
            <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={() => setAdding(false)}>İptal</button>
          </div>
        </form>
      ) : (
        <button className="btn btn--secondary" style={{ width: '100%', marginTop: 12 }} onClick={() => setAdding(true)}>
          + Kontak Ekle
        </button>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// FATURALANDIRMA SEKMESİ
// ──────────────────────────────────────────────────────────────────

const fmtCurrency = (val, currency = 'USD') => {
  if (val === null || val === undefined || val === '') return '—';
  const locale = currency === 'TRY' ? 'tr-TR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(Number(val) || 0);
};
// Geri uyumluluk için (eski kod yerlerinden çağrılmaya devam ederse)
const fmtUSD = (val) => fmtCurrency(val, 'USD');

const monthLabel = (offset = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
};

const fmtDateTr = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const STRIPE_STATUS = {
  draft: { label: 'Taslak', color: '#9A9A92' },
  open: { label: 'Gönderildi', color: '#D4FF3A' },
  paid: { label: 'Ödendi', color: '#34D399' },
  uncollectible: { label: 'Tahsil edilemedi', color: '#F2B84B' },
  void: { label: 'İptal', color: '#9A9A92' },
  failed: { label: 'Başarısız', color: '#FF6B5B' },
};

function BillingTab({ accountId, isAdmin, stripeCustomerId, contacts = [], onChange }) {
  const [sub, setSub] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, snapRes] = await Promise.all([
        api.get(`/accounts/${accountId}/subscription`),
        api.get(`/accounts/${accountId}/billing/snapshots`),
      ]);
      setSub(subRes.data);
      setSnapshots(snapRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const saveContract = async (patch) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const res = await api.put(`/accounts/${accountId}/subscription`, patch);
      setSub(res.data);
      if (onChange) onChange();
    } catch (err) {
      alert('Kaydedilemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };


  // Finalize — charge modu artık firma ayarından (Faturalandırma sekmesi) okunur, sorulmaz.
  const finalizeMonth = async ({ year, month, revenueInput }) => {
    if (!isAdmin) return;
    const monthName = new Date(year, month - 1, 1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
    const modeLabel = (sub.charge_mode || 'auto') === 'manual' ? 'Manuel (ödeme linki)' : 'Otomatik (karttan çek)';
    const checks = [
      stripeCustomerId
        ? `✓ Stripe Customer ID: ${stripeCustomerId}`
        : 'Stripe Customer ID BOŞ — fatura e-postayla eşleştirilecek; eşleşmezse Stripe\'ta yeni (kartsız) müşteri açılabilir. Önce Genel sekmesinden "Senkronize Et" önerilir.',
      '• Amazon ciro senkronu fatura öncesi elle çalıştırıldı mı? (ABD saat farkı)',
    ].join('\n');
    if (!window.confirm(`${monthName} ayını finalize edip Stripe faturasını keseceğim.\nTahsilat modu: ${modeLabel}\n\n${checks}\n\nDevam edilsin mi?`)) return;
    setFinalizing(true);
    try {
      const body = { year, month };
      if (revenueInput !== undefined && revenueInput !== null) body.total_revenue_input = revenueInput;
      await api.post(`/accounts/${accountId}/billing/snapshots`, body);
      await load();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert('Finalize başarısız: ' + msg);
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) return <div className="page-loading">Yükleniyor...</div>;
  if (!sub) return <div className="text-muted">Faturalandırma bilgisi yüklenemedi.</div>;

  const currency = sub.currency || 'USD';
  const preview = sub.live_preview || {};
  const current = preview.current || {};
  const last = preview.last || {};

  const now = new Date();
  const currYear = now.getFullYear();
  const currMonth = now.getMonth() + 1;
  const prevDate = new Date(currYear, currMonth - 2, 1);
  const prevYear = prevDate.getFullYear();
  const prevMonth = prevDate.getMonth() + 1;

  const isMonthFinalized = (y, m) =>
    snapshots.some(s => s.year === y && s.month === m);

  return (
    <div>
      {/* Sözleşme Kalemleri */}
      <BillingSection title="Sözleşme Kalemleri" hint={isAdmin ? null : 'Sadece admin ve finans düzenleyebilir'}>
        <BillingRow
          label="Sözleşme İmza Tarihi"
          value={sub.billing_anchor_date}
          format="date"
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ billing_anchor_date: v })}
        />
        <BillingRow
          label="Fatura Ödeme Günü"
          hint="(her ayın N. günü, 1-31)"
          value={sub.invoice_payment_day}
          format="day"
          editable={isAdmin}
          saving={saving}
          placeholder="Belirtilmedi"
          onSave={(v) => saveContract({ invoice_payment_day: v })}
        />
        <BillingRow
          label="US Account Management Ücreti"
          value={sub.us_am_fee}
          format="currency"
          currency={currency}
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ us_am_fee: v })}
        />
        <BillingRow
          label="Ciro Yüzdesi"
          value={sub.revenue_percentage}
          format="percent"
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ revenue_percentage: v })}
        />
        <BillingRow
          label="Ciro Eşiği"
          value={sub.revenue_threshold}
          format="currency"
          currency={currency}
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ revenue_threshold: v })}
        />
        <BillingRow
          label="Para Birimi"
          value={currency}
          format="select"
          options={['USD', 'EUR', 'GBP', 'TRY', 'CAD']}
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ currency: v })}
        />
        <BillingRow
          label="Tahsilat (Charge) Modu"
          hint="(otomatik fatura kesimi buna göre çalışır)"
          value={sub.charge_mode || 'auto'}
          format="select"
          options={[
            { value: 'auto', label: 'Otomatik — karttan çek' },
            { value: 'manual', label: 'Manuel — ödeme linki' },
          ]}
          editable={isAdmin}
          saving={saving}
          onSave={(v) => saveContract({ charge_mode: v })}
          last
        />
      </BillingSection>

      {/* Faturalandırma Kontağı */}
      <BillingContactSection
        contacts={contacts}
        selectedIds={sub.billing_contact_ids || (sub.billing_contact_id ? [sub.billing_contact_id] : [])}
        editable={isAdmin}
        saving={saving}
        onSave={(ids) => saveContract({ billing_contact_ids: ids })}
      />

      {/* Amazon Cirosu — aylık ledger (artarak, scroll) */}
      <MonthlyRevenueSection
        accountId={accountId}
        currency={currency}
        anchor={sub.billing_anchor_date}
        editable={isAdmin}
        onSaved={load}
      />

      {/* Hesaplanan Ücret (live preview, prorata dahil) */}
      <BillingSection title="Hesaplanan Ücret (Tahmini)">
        <FeePreviewRow
          label={`Geçen Ay (${monthLabel(-1)})`}
          calc={last}
          currency={currency}
          finalized={isMonthFinalized(prevYear, prevMonth)}
          canFinalize={isAdmin && !isMonthFinalized(prevYear, prevMonth)}
          finalizing={finalizing}
          onFinalize={() => finalizeMonth({ year: prevYear, month: prevMonth, revenueInput: sub.last_month_revenue })}
        />
        <FeePreviewRow
          label="Bu Ay (MTD)"
          calc={current}
          currency={currency}
          finalized={isMonthFinalized(currYear, currMonth)}
          canFinalize={isAdmin && !isMonthFinalized(currYear, currMonth)}
          finalizing={finalizing}
          onFinalize={() => finalizeMonth({ year: currYear, month: currMonth, revenueInput: sub.current_month_revenue })}
          last
        />
      </BillingSection>

      {/* Notlar / Özel Şartlar */}
      <NotesSection
        value={sub.notes || ''}
        editable={isAdmin}
        saving={saving}
        onSave={(v) => saveContract({ notes: v })}
      />

      {/* Aylık Fatura Snapshot'ları + Stripe */}
      <SnapshotsSection snapshots={snapshots} currency={currency} />

      <p style={{ fontSize: 12, color: '#9A9A92', marginTop: 16, lineHeight: 1.6 }}>
        <Icon name="info" size={14} /> Ciro alanları ileride <strong>SP-API + Advertising API</strong> üzerinden n8n ile otomatik
        güncellenecek. Şimdilik elle gir, ay toplamını yaz — backend prorata uygular.<br />
        <strong>Stripe akışı:</strong> "Ayı Finalize Et" → snapshot oluşur, n8n Stripe'da faturayı keser ve
        <strong>anında</strong> müşteriye mail gider (PDF + vade ibaresi). Vade tarihi sadece faturada yazan etikettir.<br />
        <strong>Komisyon kuralı:</strong> İmza ayında base ve ciro <code>(kalan_gün / ay_gün)</code> oranında kısılır;
        komisyon eşiği aşan kısımdan alınır: <code>(efektif_ciro − eşik) × %</code>.
      </p>

    </div>
  );
}

// Finalize öncesi charge modu seçimi (otomatik karttan çek / manuel ödeme linki)
// Aylık ciro ledger — her ay ayrı satır, artarak birikir, scroll edilebilir.
function MonthlyRevenueSection({ accountId, currency, anchor, editable, onSaved }) {
  const [rows, setRows] = useState(null); // { "y-m": revenue }
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/accounts/${accountId}/monthly-revenue`);
      const map = {};
      res.data.forEach(r => { map[`${r.year}-${r.month}`] = Number(r.revenue); });
      setRows(map);
    } catch (e) { setRows({}); }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  // Aylar: sözleşme imza ayından bugüne (yeniden eskiye)
  const months = useMemo(() => {
    const now = new Date();
    let start = anchor
      ? new Date(new Date(anchor).getFullYear(), new Date(anchor).getMonth(), 1)
      : new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const list = [];
    let d = new Date(now.getFullYear(), now.getMonth(), 1);
    let guard = 0;
    while (d >= start && guard < 240) {
      list.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
      d = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      guard++;
    }
    return list;
  }, [anchor]);

  const commit = async (year, month) => {
    setEditingKey(null);
    const val = draft === '' ? 0 : Number(draft);
    if (Number.isNaN(val)) { alert('Geçersiz ciro'); return; }
    try {
      await api.put(`/accounts/${accountId}/monthly-revenue`, { year, month, revenue: val });
      setRows(prev => ({ ...(prev || {}), [`${year}-${month}`]: val }));
      if (onSaved) onSaved();
    } catch (e) {
      alert('Kaydedilemedi: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <BillingSection title="Amazon Cirosu (Aylık)" hint="Her ay ayrı satır · scroll edilebilir">
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {rows === null ? (
          <div style={{ padding: 14, color: '#9A9A92', fontSize: 13 }}>Yükleniyor...</div>
        ) : months.map((m, i) => {
          const key = `${m.year}-${m.month}`;
          const rev = rows[key];
          const label = new Date(m.year, m.month - 1, 1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
          const isEditing = editingKey === key;
          return (
            <div key={key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 16px', borderBottom: i === months.length - 1 ? 'none' : '1px solid #17191C',
            }}>
              <span style={{ color: '#C9C7BE', fontSize: 14 }}>{label}</span>
              {isEditing ? (
                <input
                  autoFocus type="number" step="0.01" value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commit(m.year, m.month); if (e.key === 'Escape') setEditingKey(null); }}
                  onBlur={() => commit(m.year, m.month)}
                  style={{ width: 140, padding: '6px 10px', background: '#17191C', border: '1px solid #D4FF3A', borderRadius: 6, color: '#F2EFE6', fontSize: 14, textAlign: 'right', outline: 'none' }}
                />
              ) : (
                <span
                  onClick={() => { if (!editable) return; setDraft(rev != null ? String(rev) : ''); setEditingKey(key); }}
                  style={{ color: rev != null ? '#F2EFE6' : '#74746D', fontSize: 14, fontWeight: 600, cursor: editable ? 'pointer' : 'default', padding: '4px 8px', borderRadius: 6 }}
                >
                  {rev != null ? fmtCurrency(rev, currency) : (editable ? '+ Gir' : '—')}
                  {editable && <span style={{ marginLeft: 8, fontSize: 12, color: '#9A9A92' }}>✎</span>}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </BillingSection>
  );
}

function SnapshotsSection({ snapshots, currency }) {
  if (!snapshots || snapshots.length === 0) {
    return (
      <BillingSection title="Aylık Faturalar (Stripe)">
        <div style={{ padding: 16, color: '#9A9A92', fontSize: 13 }}>
          Henüz finalize edilmiş bir ay yok. Yukarıdan "Ayı Finalize Et" diyerek Stripe faturası kestir.
        </div>
      </BillingSection>
    );
  }

  return (
    <BillingSection title="Aylık Faturalar (Stripe)">
      {snapshots.map((s, i) => {
        const status = STRIPE_STATUS[s.stripe_status] || { label: s.stripe_status || 'Beklemede', color: '#9A9A92' };
        const monthName = new Date(s.year, s.month - 1, 1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
        return (
          <div key={s.id} style={{
            padding: '14px 16px', borderBottom: i === snapshots.length - 1 ? 'none' : '1px solid #17191C',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#F2EFE6', fontSize: 14, fontWeight: 600 }}>
                  {monthName}
                  {s.is_prorated && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#F2B84B' }}>
                      ◐ Kısmi Ay ({s.proration_days}/{s.days_in_month} gün)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#9A9A92', marginTop: 2 }}>
                  {s.period_start && new Date(s.period_start).toLocaleDateString('tr-TR')} – {s.period_end && new Date(s.period_end).toLocaleDateString('tr-TR')}
                  {s.finalized_by_name && ` · ${s.finalized_by_name}`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#F2EFE6', fontSize: 15, fontWeight: 700 }}>
                  {fmtCurrency(s.total_fee, s.currency || currency)}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', marginTop: 4 }}>
                  <span className="badge" style={{
                    background: status.color + '22', color: status.color,
                    fontSize: 11, fontWeight: 600, padding: '2px 8px',
                  }}>
                    {status.label}
                  </span>
                  {s.stripe_invoice_url && (
                    <a href={s.stripe_invoice_url} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#D4FF3A', fontSize: 11, textDecoration: 'none' }}>
                      Stripe ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
            {/* Audit detay */}
            <details style={{ marginTop: 8, fontSize: 11, color: '#9A9A92' }}>
              <summary style={{ cursor: 'pointer' }}>Hesap kırılımı</summary>
              <div style={{ paddingLeft: 12, paddingTop: 6, lineHeight: 1.6 }}>
                Base: {fmtCurrency(s.base_fee, s.currency || currency)}
                {s.is_prorated && ` × (${s.proration_days}/${s.days_in_month}) = ${fmtCurrency(Number(s.base_fee) * s.proration_days / s.days_in_month, s.currency || currency)}`}
                <br />
                Ciro (ay toplamı): {fmtCurrency(s.total_revenue_input, s.currency || currency)} →
                efektif: {fmtCurrency(s.effective_revenue, s.currency || currency)}
                {s.threshold_met
                  ? ` ≥ eşik ${fmtCurrency(s.revenue_threshold, s.currency || currency)} → (efektif − eşik) × %${s.revenue_percentage} = ${fmtCurrency(s.percentage_amount, s.currency || currency)}`
                  : ` < eşik ${fmtCurrency(s.revenue_threshold, s.currency || currency)} (yüzde uygulanmadı)`}
                <br />
                <strong style={{ color: '#C9C7BE' }}>Total: {fmtCurrency(s.total_fee, s.currency || currency)}</strong>
                {s.stripe_invoice_id && <><br />Stripe Invoice ID: <code>{s.stripe_invoice_id}</code></>}
                {s.paid_at && <><br />Ödeme: {new Date(s.paid_at).toLocaleString('tr-TR')}</>}
              </div>
            </details>
          </div>
        );
      })}
    </BillingSection>
  );
}

function FeePreviewRow({ label, calc, currency, finalized, canFinalize, finalizing, onFinalize, last }) {
  const fee = Number(calc.total_fee) || 0;
  const base = Number(calc.base_fee) || 0;
  const pct = Number(calc.revenue_percentage) || 0;
  const isProrated = !!calc.is_prorated;
  const thresholdMet = !!calc.threshold_met;

  const proratedBaseAmt = isProrated
    ? (Number(calc.prorated_base) || (base * (calc.proration_days / calc.days_in_month)))
    : base;
  const commissionAmt = Number(calc.percentage_amount) || 0;

  // Net tutarlarla göster (ham "× 25/30" yerine): "Sabit $1.250 (23 gün prorata) + Komisyon $625 (%5)"
  const formula = (() => {
    const baseStr = isProrated
      ? `Sabit ${fmtCurrency(proratedBaseAmt, currency)} (kısmi ay: ${calc.proration_days}/${calc.days_in_month} gün)`
      : `Sabit ${fmtCurrency(base, currency)}`;
    if (thresholdMet && commissionAmt > 0) {
      return `${baseStr} + Komisyon ${fmtCurrency(commissionAmt, currency)} (%${pct})`;
    }
    return `${baseStr} · komisyon yok (ciro eşik altı)`;
  })();

  return (
    <div style={{
      padding: '14px 16px', borderBottom: last ? 'none' : '1px solid #17191C',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ color: '#C9C7BE', fontSize: 14, flex: 1 }}>
          {label}
          {isProrated && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#F2B84B' }}>
              ◐ Kısmi Ay ({calc.proration_days}/{calc.days_in_month} gün)
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge" style={{
            background: thresholdMet ? '#34D39922' : '#9A9A9222',
            color: thresholdMet ? '#34D399' : '#9A9A92',
            fontSize: 14, fontWeight: 700, padding: '4px 12px',
          }}>
            {fmtCurrency(fee, currency)}
          </span>
          {finalized ? (
            <span style={{ fontSize: 11, color: '#34D399' }} title="Bu ay finalize edildi">✓ Finalize</span>
          ) : canFinalize ? (
            <button
              onClick={onFinalize}
              disabled={finalizing}
              style={{
                padding: '6px 12px', fontSize: 12,
                background: '#D4FF3A', color: '#0E0F11', border: 'none',
                borderRadius: 6, cursor: finalizing ? 'wait' : 'pointer', fontWeight: 600,
              }}
            >
              {finalizing ? 'İşleniyor...' : 'Ayı Finalize Et'}
            </button>
          ) : null}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#9A9A92', marginTop: 4 }}>= {formula}</div>
    </div>
  );
}

function BillingContactSection({ contacts, selectedIds = [], editable, saving, onSave }) {
  const primary = contacts.find(c => c.is_primary) || contacts[0] || null;
  const selected = selectedIds.map(Number);
  const selectedContacts = contacts.filter(c => selected.includes(c.id));
  const effectiveList = selectedContacts.length ? selectedContacts : (primary ? [primary] : []);

  const toggle = (id) => {
    if (!editable || saving) return;
    const next = selected.includes(id)
      ? selected.filter(x => x !== id)
      : [...selected, id];
    onSave(next);
  };

  return (
    <BillingSection
      title="Faturalandırma Kontakları"
      hint={selected.length
        ? `Stripe faturası ${selected.length} kontağa gönderilir`
        : (primary ? '↳ Primary kontak fallback' : 'Hesapta kontak yok')}
    >
      <div style={{ padding: '12px 16px' }}>
        {contacts.length === 0 ? (
          <div style={{ color: '#9A9A92', fontSize: 13 }}>
            Bu hesapta kayıtlı kontak yok. Kontaklar sekmesinden ekle.
          </div>
        ) : editable ? (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {contacts.map(c => {
                const on = selected.includes(c.id);
                return (
                  <label key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: saving ? 'wait' : 'pointer',
                    padding: '8px 10px', borderRadius: 6,
                    background: on ? '#C2EE1F22' : '#17191C',
                    border: `1px solid ${on ? '#D4FF3A' : '#2A2D32'}`,
                  }}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={saving}
                      onChange={() => toggle(c.id)}
                      style={{ accentColor: '#D4FF3A', width: 16, height: 16 }}
                    />
                    <span style={{ color: '#F2EFE6', fontSize: 13 }}>
                      {c.name} <span style={{ color: '#9A9A92' }}>— {c.email}</span>
                      {c.role ? <span style={{ color: '#74746D' }}> ({c.role})</span> : null}
                      {c.is_primary ? <span style={{ color: '#F2B84B', marginLeft: 4 }}>★</span> : null}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#C9C7BE' }}>
              <Icon name="mail" size={14} /> Fatura gidecek: <strong style={{ color: '#F2EFE6' }}>
                {effectiveList.length ? effectiveList.map(c => c.email).join(', ') : '—'}
              </strong>
              {!selected.length && primary && <span style={{ color: '#9A9A92' }}> (Primary fallback)</span>}
            </div>
          </div>
        ) : (
          <div style={{ color: '#C9C7BE', fontSize: 14 }}>
            {effectiveList.length
              ? effectiveList.map(c => `${c.name} — ${c.email}`).join(', ')
              : '—'}
            {!selected.length && primary && <span style={{ marginLeft: 8, fontSize: 11, color: '#9A9A92' }}>(Primary)</span>}
          </div>
        )}
      </div>
    </BillingSection>
  );
}

function NotesSection({ value, editable, saving, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = () => {
    if (!editable) return;
    setDraft(value || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    const current = (value || '').trim();
    if (next === current) return;
    onSave(next);
  };

  return (
    <BillingSection
      title="Notlar / Özel Şartlar"
      hint={editable ? null : 'Sadece admin düzenleyebilir'}
    >
      <div style={{ padding: '14px 16px' }}>
        {editing ? (
          <div>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
              }}
              placeholder={`Örn: "5 parent'a kadar 1500€, 6+ 2000€" — özel sözleşme şartları, tier komisyon notu, vb.`}
              style={{
                width: '100%', minHeight: 80,
                padding: '10px 12px', background: '#17191C',
                border: '1px solid #D4FF3A', borderRadius: 6,
                color: '#F2EFE6', fontSize: 13, outline: 'none',
                resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                onClick={commit}
                disabled={saving}
                style={{
                  padding: '6px 14px', fontSize: 12,
                  background: '#D4FF3A', color: '#0E0F11', border: 'none',
                  borderRadius: 6, cursor: saving ? 'wait' : 'pointer', fontWeight: 600,
                }}
              >
                {saving ? '...' : 'Kaydet'}
              </button>
              <button
                onClick={() => setEditing(false)}
                style={{
                  padding: '6px 14px', fontSize: 12,
                  background: 'transparent', color: '#9A9A92',
                  border: '1px solid #2A2D32', borderRadius: 6, cursor: 'pointer',
                }}
              >
                İptal
              </button>
              <span style={{ fontSize: 11, color: '#74746D', alignSelf: 'center' }}>
                Cmd/Ctrl+Enter: kaydet · Esc: iptal
              </span>
            </div>
          </div>
        ) : (
          <div
            onClick={startEdit}
            title={editable ? 'Düzenlemek için tıkla' : null}
            style={{
              cursor: editable ? 'pointer' : 'default',
              color: value ? '#F2EFE6' : '#74746D',
              fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
              padding: '4px 0', minHeight: 24,
            }}
          >
            {value || (editable ? '+ Not eklemek için tıkla' : '—')}
            {value && editable && (
              <span style={{ marginLeft: 8, fontSize: 11, color: '#9A9A92' }}>✎</span>
            )}
          </div>
        )}
      </div>
    </BillingSection>
  );
}

function BillingSection({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{
          color: '#C9C7BE', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, margin: 0,
        }}>{title}</h3>
        {hint && <span style={{ fontSize: 11, color: '#9A9A92' }}>{hint}</span>}
      </div>
      <div style={{ background: '#0A0B0D', border: '1px solid #17191C', borderRadius: 8, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

function BillingRow({ label, value, format, currency, editable, saving, onSave, placeholder, last, options, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Dropdown (currency vb.) — tıkla-düzenle yerine kalıcı <select>
  if (format === 'select') {
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', borderBottom: last ? 'none' : '1px solid #17191C',
      }}>
        <span style={{ color: '#C9C7BE', fontSize: 14 }}>{label}</span>
        {editable ? (
          <select
            value={value || ''}
            disabled={saving}
            onChange={(e) => onSave(e.target.value)}
            style={{
              padding: '6px 10px', background: '#17191C', border: '1px solid #2A2D32',
              borderRadius: 6, color: '#F2EFE6', fontSize: 14, outline: 'none',
              cursor: saving ? 'wait' : 'pointer', minWidth: 120,
            }}
          >
            {(options || []).map(o => {
              const val = typeof o === 'object' ? o.value : o;
              const lbl = typeof o === 'object' ? o.label : o;
              return <option key={val} value={val}>{lbl}</option>;
            })}
          </select>
        ) : (
          <span style={{ color: '#F2EFE6', fontSize: 14, fontWeight: 600 }}>
            {(() => {
              const found = (options || []).find(o => (typeof o === 'object' ? o.value : o) === value);
              return found ? (typeof found === 'object' ? found.label : found) : (value || '—');
            })()}
          </span>
        )}
      </div>
    );
  }

  const displayValue = () => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">{placeholder || '—'}</span>;
    }
    if (format === 'currency') return fmtCurrency(value, currency || 'USD');
    if (format === 'usd') return fmtCurrency(value, 'USD');
    if (format === 'percent') return `%${Number(value)}`;
    if (format === 'date') return fmtDateTr(value);
    if (format === 'day') return `Her ayın ${Number(value)}. günü`;
    if (format === 'text') return String(value);
    return value;
  };

  const startEdit = () => {
    if (!editable) return;
    let initial = '';
    if (value !== null && value !== undefined) {
      if (format === 'date') {
        // ISO YYYY-MM-DD for <input type="date">
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          initial = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
      } else {
        initial = String(value);
      }
    }
    setDraft(initial);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (format === 'date') {
      const next = draft === '' ? null : draft;
      if (next === value) return;
      onSave(next);
      return;
    }
    if (format === 'text') {
      const next = draft === '' ? null : draft;
      if (next === value) return;
      onSave(next);
      return;
    }
    const num = draft === '' ? null : Number(draft);
    if (num !== null && Number.isNaN(num)) {
      alert('Geçersiz sayı');
      return;
    }
    if (format === 'day' && num !== null && (!Number.isInteger(num) || num < 1 || num > 31)) {
      alert('Ödeme günü 1-31 arası bir tam sayı olmalı');
      return;
    }
    if (num === Number(value)) return;
    onSave(num);
  };

  const inputType = format === 'date' ? 'date' : (format === 'text' ? 'text' : 'number');
  const inputWidth = format === 'date' ? 160 : (format === 'text' ? 80 : 140);
  const inputAlign = format === 'text' || format === 'date' ? 'left' : 'right';

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 16px', borderBottom: last ? 'none' : '1px solid #17191C',
    }}>
      <span style={{ color: '#C9C7BE', fontSize: 14 }}>
        {label}
        {hint && <span style={{ marginLeft: 8, fontSize: 11, color: '#74746D' }}>{hint}</span>}
      </span>
      {editing ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            autoFocus
            type={inputType}
            step={format === 'day' ? '1' : (inputType === 'number' ? '0.01' : undefined)}
            min={format === 'day' ? 1 : undefined}
            max={format === 'day' ? 31 : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            onBlur={commit}
            style={{
              width: inputWidth, padding: '6px 10px', background: '#17191C',
              border: '1px solid #D4FF3A', borderRadius: 6, color: '#F2EFE6',
              fontSize: 14, outline: 'none', textAlign: inputAlign,
            }}
          />
          {saving && <span style={{ color: '#9A9A92', fontSize: 12 }}>...</span>}
        </div>
      ) : (
        <div
          onClick={startEdit}
          title={editable ? 'Düzenlemek için tıkla' : 'Düzenleme yetkisi yok'}
          style={{
            display: 'flex', gap: 8, alignItems: 'center',
            color: '#F2EFE6', fontSize: 14, fontWeight: 600,
            cursor: editable ? 'pointer' : 'default',
            padding: '4px 8px', borderRadius: 6,
          }}
          onMouseEnter={(e) => { if (editable) e.currentTarget.style.background = '#17191C'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          {displayValue()}
          {editable && <span style={{ fontSize: 12, color: '#9A9A92' }}>✎</span>}
        </div>
      )}
    </div>
  );
}

